/* FLOW: global behaviour. Plain custom elements, no dependencies. 3D lives in flow-3d.js. */

const theme = window.theme || {};

/* Utilities ----------------------------------------------------------------- */

const debounce = (fn, wait = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(null, args), wait);
  };
};

const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

function formatMoney(cents, format = theme.settings?.moneyFormat || '{{amount}}') {
  const value = Number(cents) / 100;
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const kind = (format.match(pattern) || [])[1] || 'amount';
  const fmt = (n, decimals, thousands, decimal) => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  const map = {
    amount: () => fmt(value, 2, ',', '.'),
    amount_no_decimals: () => fmt(value, 0, ',', '.'),
    amount_with_comma_separator: () => fmt(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => fmt(value, 0, '.', ','),
    amount_with_space_separator: () => fmt(value, 2, ' ', ','),
    amount_with_apostrophe_separator: () => fmt(value, 2, "'", '.'),
  };
  return format.replace(pattern, (map[kind] || map.amount)());
}

function showToast(message) {
  const toast = document.querySelector('.toast');
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => (toast.hidden = true), 3200);
}

/* Focus trap shared by drawers and the search modal */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* How the visitor last acted: a drawer or modal hands focus back to the button that opened it only
   after keys, since a phone (iOS) draws the keyboard ring around that button even after a tap. */
let usingKeys = false;
document.addEventListener('keydown', (e) => { if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') usingKeys = true; }, true);
document.addEventListener('pointerdown', () => { usingKeys = false; }, true);
const returnFocus = (el) => {
  if (usingKeys) el?.focus?.({ preventScroll: true });
  else if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
};

// while an overlay is open a drag outside its panel does nothing (iOS scrolls the page under it
// otherwise); the panels keep their own scrolling
document.addEventListener('touchmove', (e) => {
  if (!document.body.classList.contains('has-overlay')) return;
  if (e.target.closest?.('.drawer__panel, .search-modal__panel, .zoom')) return;
  e.preventDefault();
}, { passive: false });

/* Drawer -------------------------------------------------------------------- */

class DrawerPanel extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      trapFocus(this, e);
    });
  }

  open(trigger) {
    if (this.hasAttribute('open')) return;
    this.trigger = trigger || document.activeElement;
    this.setAttribute('open', '');
    this.removeAttribute('aria-hidden');
    document.body.classList.add('has-overlay');
    this.trigger?.setAttribute?.('aria-expanded', 'true');
    requestAnimationFrame(() => {
      (this.querySelector('[data-autofocus]') || this.querySelector('.drawer__panel'))?.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('drawer-panel[open], search-modal[open]')) document.body.classList.remove('has-overlay');
    this.trigger?.setAttribute?.('aria-expanded', 'false');
    returnFocus(this.trigger);
  }
}
customElements.define('drawer-panel', DrawerPanel);

/* Mobile menu: a submenu is its own screen; back returns; every opening starts from the top */
class MobileNav extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const open = e.target.closest('[data-open]');
      if (open) return this.show(open.dataset.open, open);
      if (e.target.closest('[data-back]')) this.show('root');
    });
    this.closest('drawer-panel')?.addEventListener('drawer:open', () => this.show('root'));
  }

  show(view, opener) {
    const from = this.dataset.view;
    this.dataset.view = view;
    this.querySelectorAll('[data-screen]').forEach((screen) => {
      const current = screen.dataset.screen === view;
      screen.classList.toggle('is-current', current && view !== 'root');
      screen.inert = !current;
      if (current) screen.scrollTop = 0;
    });
    this.querySelectorAll('[data-open]').forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.open === view)));
    // keys: into the new screen, and back to the item it came from
    // keys only (a phone would ring the button after a tap): into the new screen, and back to the
    // item it came from
    if (!usingKeys) return;
    if (view !== 'root') this.querySelector(`[data-screen="${view}"] [data-back]`)?.focus({ preventScroll: true });
    else if (from && from !== 'root') this.querySelector(`[data-open="${from}"]`)?.focus({ preventScroll: true });
  }
}
customElements.define('mobile-nav', MobileNav);

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!drawer) return;
  e.preventDefault();
  drawer.open(opener);
});

/* Header -------------------------------------------------------------------- */

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.site-header');
    // Sticky header that steps aside: slides away while scrolling down, comes back on the way up.
    // Away from the top it gets a hairline and a faint shadow (.is-scrolled). It only turns after a
    // real run in one direction (48px down to hide, 24px up to return), so small moves, trackpad
    // jitter and iOS bounce never flip it; it never hides while keyboard focus is inside.
    if (this.wrapper?.classList.contains('is-sticky')) {
      let last = window.scrollY;
      let frame = 0;
      // sticky things below (product 3D, cart summary) step down by the header while it shows
      const publishOffset = () => {
        const showing = window.scrollY > 0 && !this.wrapper.classList.contains('is-hidden');
        document.documentElement.style.setProperty('--header-offset', showing ? `${this.wrapper.offsetHeight}px` : '0px');
      };
      let travel = 0; // distance scrolled in the current direction: + down, - up
      const update = () => {
        frame = 0;
        const y = Math.max(0, window.scrollY);
        const dy = y - last;
        last = y;
        this.wrapper.classList.toggle('is-scrolled', y > 0);
        if (dy !== 0) travel = Math.sign(dy) === Math.sign(travel) ? travel + dy : dy;
        const hidden = this.wrapper.classList.contains('is-hidden');
        const nearTop = y <= this.wrapper.offsetHeight;
        if (!hidden && travel > 48 && !nearTop && !document.body.classList.contains('has-overlay') &&
          !this.querySelector('.is-open') && !this.wrapper.contains(document.activeElement)) {
          this.wrapper.classList.add('is-hidden');
        } else if (hidden && (travel < -24 || nearTop)) {
          this.wrapper.classList.remove('is-hidden');
        }
        publishOffset();
      };
      update();
      window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
      this.wrapper.addEventListener('focusin', () => this.wrapper.classList.remove('is-hidden'));
      // the header's height, for layouts that fill the screen below it (home hero)
      const publish = () => document.documentElement.style.setProperty('--header-h', `${this.wrapper.offsetHeight}px`);
      new ResizeObserver(publish).observe(this.wrapper);
    }

    this.querySelectorAll('[data-mega]').forEach((item) => {
      const button = item.querySelector('[aria-expanded]');
      let timer;
      const set = (open) => {
        clearTimeout(timer);
        item.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
      };
      item.addEventListener('mouseenter', () => { timer = setTimeout(() => set(true), 80); });
      item.addEventListener('mouseleave', () => set(false));
      item.addEventListener('focusin', () => set(true));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { set(false); button.focus(); }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) set(false);
      });
    });
  }
}
customElements.define('site-header', SiteHeader);

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.announcement__item')];
    if (this.items.length < 2) return;
    this.index = 0;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
    this.addEventListener('mouseenter', () => clearInterval(this.timer));
    this.addEventListener('mouseleave', () => this.play());
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.play();
  }
  play() {
    clearInterval(this.timer);
    this.timer = setInterval(() => this.go(1), Number(this.dataset.speed || 5) * 1000);
  }
  go(step, user) {
    this.items[this.index].classList.remove('is-active');
    this.items[this.index].setAttribute('aria-hidden', 'true');
    this.index = (this.index + step + this.items.length) % this.items.length;
    this.items[this.index].classList.add('is-active');
    this.items[this.index].removeAttribute('aria-hidden');
    if (user) this.play();
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* Cart ---------------------------------------------------------------------- */

const Cart = {
  sections() {
    return [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection);
  },

  async request(url, body) {
    const sections = this.sections();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
    this.render(data.sections);
    const cart = data.item_count !== undefined && data.items ? data : await (await fetch(`${theme.routes.cart}.js`)).json();
    this.updateCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return data;
  },

  add(items) {
    return this.request(`${theme.routes.cartAdd}.js`, { items });
  },

  change(line, quantity) {
    return this.request(`${theme.routes.cartChange}.js`, { line, quantity });
  },

  /** note, discount codes and other cart attributes */
  update(body) {
    return this.request(`${theme.routes.cartUpdate}.js`, body);
  },

  render(sections) {
    if (!sections) return;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const doc = parseHTML(html);
      document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
        const source = doc.querySelector(`[data-cart-section="${id}"]`);
        if (!source) return;
        // rows the visitor opened (discount code, note) stay open through the re-render
        const open = [...target.querySelectorAll('details[data-keep][open]')].map((d) => d.dataset.keep);
        target.innerHTML = source.innerHTML;
        open.forEach((key) => target.querySelector(`details[data-keep="${key}"]`)?.setAttribute('open', ''));
      });
    }
  },

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.dataset.count = count;
    });
  },

  afterAdd(trigger) {
    if (theme.settings.cartType === 'page') {
      window.location.href = theme.routes.cart;
      return;
    }
    const drawer = document.getElementById('CartDrawer');
    if (drawer) drawer.open(trigger);
    else showToast(theme.strings.added);
  },
};
window.FlowCart = Cart;

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-quantity]');
      if (input) this.update(input.dataset.line, Number(input.value));
    });
    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-line-remove]');
      if (remove) {
        e.preventDefault();
        this.update(remove.dataset.line, 0);
      }
    });
  }

  async update(line, quantity) {
    const row = this.querySelector(`[data-line-item="${line}"]`);
    row?.classList.add('is-updating');
    try {
      await Cart.change(Number(line), quantity);
    } catch (err) {
      row?.classList.remove('is-updating');
      showToast(err.message);
    }
  }
}
customElements.define('cart-items', CartItems);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-step]');
      if (!btn) return;
      e.preventDefault();
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const prev = Number(this.input.value);
      const next = Math.min(max, Math.max(min, prev + Number(btn.dataset.step)));
      if (next === prev) return;
      this.input.value = next;
      // the number rolls in from the direction it moved
      this.input.classList.remove('qty-up', 'qty-down');
      void this.input.offsetWidth;
      this.input.classList.add(next > prev ? 'qty-up' : 'qty-down');
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* Product form (PDP, featured product). Variant id comes from the form input. */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => this.submit(e));
  }

  async submit(e) {
    e.preventDefault();
    const button = this.form.querySelector('[type="submit"]');
    if (this.form.querySelector('[data-awaiting-choice]')) {
      this.dispatchEvent(new CustomEvent('product-form:choose', { bubbles: true }));
      return;
    }
    if (button.getAttribute('aria-disabled') === 'true') {
      this.dispatchEvent(new CustomEvent('product-form:invalid', { bubbles: true }));
      return;
    }
    button.classList.add('is-loading');
    const data = new FormData(this.form);
    const errorBox = this.querySelector('[data-form-error]');
    if (errorBox) errorBox.hidden = true;
    try {
      const properties = {};
      for (const [key, value] of data.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value) properties[m[1]] = value;
      }
      const animated = button.classList.contains('add-button') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // the dots bounce long enough to read as a step, never a flicker on a fast response
      const shortest = new Promise((r) => setTimeout(r, animated ? 480 : 0));
      await Cart.add([{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties }]);
      await shortest;
      Cart.afterAdd(button);
    } catch (err) {
      if (errorBox) {
        errorBox.textContent = err.message;
        errorBox.hidden = false;
      } else showToast(err.message);
    } finally {
      button.classList.remove('is-loading');
    }
  }
}
customElements.define('product-form', ProductForm);

/* Quick add from product cards: one button per size */
document.addEventListener('click', async (e) => {
  const button = e.target.closest('[data-quick-add]');
  if (!button) return;
  e.preventDefault();
  button.classList.add('is-loading');
  try {
    await Cart.add([{ id: Number(button.dataset.quickAdd), quantity: 1 }]);
    Cart.afterAdd(button);
  } catch (err) {
    showToast(err.message);
  } finally {
    button.classList.remove('is-loading');
  }
});

/* Cart extras: order note and discount codes (drawer and cart page) ----------- */

// the note saves a moment after typing stops; nothing re-renders, so the field keeps focus
document.addEventListener('input', debounce((e) => {
  if (!e.target.matches?.('[data-cart-note]')) return;
  fetch(`${theme.routes.cartUpdate}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: e.target.value }),
  });
}, 500));

// a code is applied through /cart/update.js; Shopify answers with the cart, which says whether it took
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-discount-form]');
  if (!form) return;
  e.preventDefault();
  const code = new FormData(form).get('discount')?.toString().trim();
  if (!code) return;
  const button = form.querySelector('[type="submit"]');
  button?.classList.add('is-loading');
  try {
    const applied = [...document.querySelectorAll('[data-discount-remove]')].map((b) => b.dataset.discountRemove);
    const cart = await Cart.update({ discount: [...new Set([...applied, code])].join(',') });
    const found = cart.discount_codes?.find((d) => d.code.toLowerCase() === code.toLowerCase());
    if (found?.applicable) {
      // it took: the row (kept open through the re-render) glides shut and the code shows on it
      document.querySelectorAll('[data-discount-applied]').forEach((tag) => tag.classList.add('is-new'));
      document.querySelectorAll('details[data-keep="discount"][open]').forEach((d) => glide(d, false));
    } else {
      // the cart has re-rendered by now (its row kept open): show the message in the fresh form,
      // give the code back and nudge the field
      document.querySelectorAll('[data-discount-error]').forEach((p) => (p.hidden = false));
      document.querySelectorAll('[data-discount-form] input[name="discount"]').forEach((i) => {
        i.value = code;
        i.classList.remove('is-invalid');
        void i.offsetWidth;
        i.classList.add('is-invalid');
      });
    }
  } catch (err) {
    showToast(err.message);
  } finally {
    button?.classList.remove('is-loading');
  }
});

document.addEventListener('click', async (e) => {
  const remove = e.target.closest('[data-discount-remove]');
  if (!remove) return;
  const keep = [...document.querySelectorAll('[data-discount-remove]')].map((b) => b.dataset.discountRemove).filter((c) => c !== remove.dataset.discountRemove);
  try {
    await Cart.update({ discount: keep.join(',') });
  } catch (err) {
    showToast(err.message);
  }
});

// typing a new code clears the last one's error
document.addEventListener('input', (e) => {
  if (!e.target.matches?.('[data-discount-form] input[name="discount"]')) return;
  e.target.classList.remove('is-invalid');
  e.target.closest('[data-discount-form]').querySelector('[data-discount-error]')?.setAttribute('hidden', '');
});

// the cart's discount and note rows and every accordion (FAQ, product details) open and close with
// a glide (a plain <details> only snaps)
function glide(details, open) {
  const body = details.querySelector(':scope > .cart-addon__body, :scope > .accordion__content');
  if (details.open === open || details.dataset.moving) return;
  if (!body || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    details.open = open;
    return;
  }
  details.dataset.moving = '';
  // quick and light: opening starts fast and settles, closing is shorter still; the contents drop in
  // a few pixels as the row opens, so the eye follows them rather than the row moving. Taller
  // contents (a product description) take a little longer, so every row moves at a similar speed.
  const pad = getComputedStyle(body).paddingBottom;
  const span = (height) => Math.round(Math.min(420, 200 + height / 4));
  if (!open) {
    details.classList.add('is-closing');
    body.animate([{ height: `${body.offsetHeight}px`, paddingBottom: pad, opacity: 1 }, { height: '0px', paddingBottom: '0px', opacity: 0 }], { duration: span(body.offsetHeight) * 0.85, easing: 'cubic-bezier(0.4, 0, 1, 1)' }).onfinish = () => {
      details.open = false;
      details.classList.remove('is-closing');
      delete details.dataset.moving;
    };
  } else {
    details.open = true;
    const timing = { duration: span(body.offsetHeight), easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' };
    body.animate([{ height: '0px', paddingBottom: '0px' }, { height: `${body.offsetHeight}px`, paddingBottom: pad }], timing).onfinish = () => {
      delete details.dataset.moving;
    };
    [...body.children].forEach((child) => child.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { ...timing, duration: timing.duration + 40 }));
  }
}
document.addEventListener('click', (e) => {
  const summary = e.target.closest('.cart-addon > summary, .accordion > summary');
  if (!summary) return;
  e.preventDefault();
  glide(summary.parentElement, !summary.parentElement.open);
});

/* Recently viewed (per browser) */
const Recent = {
  key: 'atelier:recent',
  push(handle) {
    try {
      const list = JSON.parse(localStorage.getItem(this.key)) || [];
      localStorage.setItem(this.key, JSON.stringify([handle, ...list.filter((h) => h !== handle)].slice(0, 12)));
    } catch {
      /* ignore */
    }
  },
  read() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch {
      return [];
    }
  },
};
window.FlowRecent = Recent;

/* Predictive search ----------------------------------------------------------- */

class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultContent = this.querySelector('[data-search-default]');
    this.cache = new Map();

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') this.moveSelection(e);
      trapFocus(this, e);
    });
    this.input?.addEventListener('input', debounce(() => this.search(), 220));

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-search-open]');
      if (!opener) return;
      e.preventDefault();
      this.open(opener);
    });
    // "/" or Ctrl/⌘+K opens search, unless the visitor is typing somewhere
    document.addEventListener('keydown', (e) => {
      const t = e.target;
      if (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === '/' || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        this.open(document.querySelector('[data-search-open]'));
      }
    });
  }

  open(trigger) {
    this.trigger = trigger;
    this.setAttribute('open', '');
    document.body.classList.add('has-overlay');
    requestAnimationFrame(() => this.input?.focus());
  }

  close() {
    this.removeAttribute('open');
    if (!document.querySelector('drawer-panel[open]')) document.body.classList.remove('has-overlay');
    returnFocus(this.trigger);
  }

  async search() {
    const q = this.input.value.trim();
    if (!q) {
      this.results.hidden = true;
      if (this.defaultContent) this.defaultContent.hidden = false;
      return;
    }
    if (this.dataset.predictive !== 'true') return;
    const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=8&resources[limit_scope]=each`;
    try {
      let html = this.cache.get(url);
      if (!html) {
        const res = await fetch(url);
        if (!res.ok) return;
        html = await res.text();
        this.cache.set(url, html);
      }
      if (this.input.value.trim() !== q) return;
      const content = parseHTML(html).querySelector('[data-predictive-content]');
      this.results.innerHTML = content ? content.innerHTML : '';
      this.results.hidden = false;
      if (this.defaultContent) this.defaultContent.hidden = true;
    } catch {
      /* network error: the form still submits to the search page */
    }
  }

  moveSelection(e) {
    const options = [...this.results.querySelectorAll('[role="option"]')];
    if (!options.length || this.results.hidden) return;
    e.preventDefault();
    const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
    const next = e.key === 'ArrowDown' ? Math.min(options.length - 1, current + 1) : Math.max(0, current - 1);
    options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    options[next].setAttribute('aria-selected', 'true');
    options[next].querySelector('a')?.focus();
  }
}
customElements.define('search-modal', SearchModal);

/* Slider controls for native scroll-snap rows ------------------------------- */

class SliderControls extends HTMLElement {
  connectedCallback() {
    this.slider = document.getElementById(this.dataset.for);
    if (!this.slider) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    const step = () => this.slider.firstElementChild?.getBoundingClientRect().width || this.slider.clientWidth;
    this.prev?.addEventListener('click', () => this.slider.scrollBy({ left: -step(), behavior: 'smooth' }));
    this.next?.addEventListener('click', () => this.slider.scrollBy({ left: step(), behavior: 'smooth' }));
    const update = () => {
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.slider.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.slider.scrollLeft >= max;
      this.hidden = max <= 0;
    };
    this.slider.addEventListener('scroll', debounce(update, 60), { passive: true });
    window.addEventListener('resize', debounce(update, 150));
    update();
  }
}
customElements.define('slider-controls', SliderControls);

/* Best sellers shelf ------------------------------------------------------------ */
/* The shelf arrives with a fallback; the whole store sorted by sales replaces it (sections/shelf-items).
   If the request fails, the fallback simply stays. */
document.querySelectorAll('[data-best-sellers]').forEach(async (grid) => {
  try {
    const res = await fetch(grid.dataset.bestSellers);
    if (!res.ok) return;
    const items = [...parseHTML(await res.text()).querySelectorAll('ul > li')].slice(0, Number(grid.dataset.limit) || 8);
    if (items.length) grid.replaceChildren(...items);
  } catch {
    /* keep the fallback */
  }
});

/* Theme editor: keep drawers usable while editing ------------------------------ */
document.addEventListener('shopify:section:select', (e) => {
  if (e.target.querySelector('#CartDrawer')) document.getElementById('CartDrawer')?.open();
});
document.addEventListener('shopify:section:deselect', (e) => {
  if (e.target.querySelector('#CartDrawer')) document.getElementById('CartDrawer')?.close();
});

window.Flow = { debounce, parseHTML, formatMoney, showToast, trapFocus };
